/** Reusable wiped chalk slate in the simple bracket of a pen gate. */
import * as THREE from 'three';
import { getZone, type ZonePalette } from '@/render/palette';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

const zone: ZonePalette = (() => {
  const found = getZone('fattening');
  if (!found) throw new Error('pen-card: fattening palette missing');
  return found;
})();

function zoneSurface(index: number): number {
  const colour = zone.surfaces[index];
  if (colour === undefined) throw new Error(`pen-card: fattening surface ${index} missing`);
  return colour;
}

function part(geometry: THREE.BufferGeometry, material: THREE.Material, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = name;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'pen-card';
  const bracketSteel = surface('metal', zoneSurface(2));
  const slate = surface('mineral', zoneSurface(0));
  const chalk = surface('mineral', zoneSurface(4));

  const bracket = new THREE.Group();
  bracket.name = 'pen-card-gate-bracket';
  const foot = part(new THREE.BoxGeometry(0.28, 0.02, 0.055), bracketSteel, 'pen-card-bracket-foot');
  foot.position.set(0, 0.01, 0);
  bracket.add(foot);
  for (const [x, name] of [[-0.13, 'pen-card-bracket-left'], [0.13, 'pen-card-bracket-right']] as const) {
    const upright = part(new THREE.BoxGeometry(0.02, 0.18, 0.035), bracketSteel, name);
    upright.position.set(x, 0.10, -0.004);
    bracket.add(upright);
  }
  group.add(bracket);

  const slateBoard = part(new THREE.BoxGeometry(0.24, 0.16, 0.012), slate, 'pen-card-slate');
  slateBoard.position.set(0, 0.11, 0.0215);
  group.add(slateBoard);

  // These are not glyphs. A couple of uneven, over-written strokes say that a
  // worker wiped and re-used the slate, leaving Beat 11 a factual prop rather
  // than a tiny piece of UI text or a font rendered into the world.
  const chalkMarks = new THREE.Group();
  chalkMarks.name = 'pen-card-chalk-marks';
  for (const [width, x, y, turn, name] of [
    [0.075, -0.055, 0.132, -0.12, 'chalk-old-slash'],
    [0.055, 0.012, 0.102, 0.07, 'chalk-fresh-stroke'],
    [0.042, 0.065, 0.081, -0.2, 'chalk-short-tail'],
  ] as const) {
    const stroke = part(new THREE.BoxGeometry(width, 0.008, 0.004), chalk, name);
    stroke.position.set(x, y, 0.030);
    stroke.rotation.z = turn;
    chalkMarks.add(stroke);
  }
  group.add(chalkMarks);

  return group;
}

export const penCardAsset: AssetDefinition = {
  id: 'pen-card',
  label: 'Pen card',
  description: 'Small slate pen card in a steel gate bracket, with wiped procedural chalk marks.',
  footprint: { width: 0.28, height: 0.20, length: 0.055 },
  create: build,
};
